import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { DialerLanguageToggle } from "@/components/connect/DialerLanguageToggle";
import { SEOHead } from "@/components/seo/SEOHead";
import { getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { CONNECT_COUNTRIES } from "@/config/connect-countries";
import { useCountryRates } from "@/hooks/useCountryRates";
import { formatAudCents } from "@/lib/money";

/**
 * Country-code SEO landing page. Targets long-tail diaspora queries
 * like "+211 country code" / "south sudan country code" — KDI <30 in
 * en-US per Semrush (2026-06-18). Reuses CONNECT_COUNTRIES config so
 * we never duplicate dial codes, capitals, or major cities.
 *
 * Route: /country-code/:slug   (e.g. /country-code/south-sudan)
 */

// Curated set of country-code guides, ordered by diaspora value +
// keyword difficulty. Slugs MUST exist in CONNECT_COUNTRIES.
export const COUNTRY_CODE_SLUGS = [
  "south-sudan",
  "ethiopia",
  "sudan",
  "eritrea",
  "kenya",
  "nigeria",
  "uganda",
  "ghana",
  "somalia",
  "zimbabwe",
  "tanzania",
  "rwanda",
] as const;

export default function CountryCodePage() {
  const { slug } = useParams<{ slug: string }>();
  const country = slug ? CONNECT_COUNTRIES[slug] : undefined;
  const { all } = useCountryRates();

  // Pull live per-minute rates from the same source the dialer uses,
  // so the article price always matches the call screen.
  const rates = useMemo(() => {
    if (!country) return { landline: null as number | null, mobile: null as number | null };
    const matches = all.filter((c) => c.countryCode === country.dialCode);
    const landline = matches.find((m) => m.destinationType !== "mobile");
    const mobile = matches.find((m) => m.destinationType === "mobile");
    return {
      landline: landline?.ratePerMinuteCents ?? null,
      mobile: mobile?.ratePerMinuteCents ?? null,
    };
  }, [all, country]);

  if (!slug) return <Navigate to="/country-code" replace />;
  if (!country) return <Navigate to="/country-code" replace />;

  const dialDigits = country.dialCode.replace(/^\+/, "");
  const exampleLocal = "212 345 678";
  const fullExample = `+${dialDigits} ${exampleLocal}`;
  const url = `https://elefa.tel/country-code/${slug}`;

  const title = `${country.dialCode}. ${country.name} country code and how to dial`;
  const description = `${country.dialCode} is the country code for ${country.name}. Here's how to dial ${country.name} from the US, UK, Europe or Australia, with current per-minute rates on elefa Connect.`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What is the country code for ${country.name}?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `The international country code for ${country.name} is ${country.dialCode}. To call ${country.name} from anywhere in the world, dial your international access code (e.g. 011 from the US, 0011 from Australia, 00 from most of Europe) followed by ${dialDigits} and then the local number.`,
        },
      },
      {
        "@type": "Question",
        name: `How do I dial a ${country.name} number from abroad?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `From any country: + ${dialDigits} + local number. Example: ${fullExample}. Drop the leading 0 from the local number when dialling internationally.`,
        },
      },
      ...country.faqs
        .filter((f) => {
          const q = f.question.toLowerCase();
          return !q.includes("what is the country code") && !q.includes("how do i dial");
        })
        .map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
    ],
  };

  const breadcrumb = getBreadcrumbSchema([
    { name: "Home", url: "https://elefa.tel/" },
    { name: "Country Codes", url: "https://elefa.tel/country-code" },
    { name: `${country.dialCode} ${country.name}`, url },
  ]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={title}
        description={description}
        canonicalUrl={getCanonicalUrl(`/country-code/${slug}`)}
        keywords={[
          `${country.name.toLowerCase()} country code`,
          `${country.dialCode} country code`,
          `how to call ${country.name.toLowerCase()}`,
          `call ${country.name.toLowerCase()}`,
          `${country.name.toLowerCase()} dialing code`,
        ].join(", ")}
        structuredData={[faqSchema, breadcrumb]}
      />

      <nav className="container py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
        </Link>
        <div className="flex items-center gap-3">
          <DialerLanguageToggle />
          <Link to="/connect-portal/auth">
            <Button variant="outline" size="sm">Get started</Button>
          </Link>
        </div>
      </nav>

      <main className="container max-w-3xl py-8">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:underline">Home</Link>
          {" / "}
          <Link to="/country-code" className="hover:underline">Country codes</Link>
          {" / "}
          <span>{country.dialCode} {country.name}</span>
        </nav>

        <header className="mb-8">
          <div className="text-6xl mb-3" aria-hidden>{country.flag}</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            {country.dialCode} {country.name} country code
          </h1>
          <p className="text-lg text-muted-foreground mt-3">
            {country.dialCode} is the international dialling code for {country.name}. what you place in
            front of a local number to reach a phone in {country.capital} or anywhere else in the country.
            If you're trying to call family, a business contact, or check a number you've been given, this page covers it.
          </p>
        </header>

        <section className="rounded-xl border bg-card p-6 mb-8">
          <h2 className="text-2xl font-semibold mb-4">How to dial {country.name}</h2>
          <dl className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Country code</dt>
              <dd className="text-lg font-mono">{country.dialCode}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ISO code</dt>
              <dd className="text-lg font-mono">{country.code}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Capital</dt>
              <dd className="text-lg">{country.capital}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Example format</dt>
              <dd className="text-lg font-mono">{fullExample}</dd>
            </div>
          </dl>

          <div className="mt-6 space-y-2 text-sm">
            <p><strong>From the US or Canada:</strong> <code className="font-mono">011 {dialDigits} {exampleLocal}</code></p>
            <p><strong>From the UK or most of Europe:</strong> <code className="font-mono">00 {dialDigits} {exampleLocal}</code></p>
            <p><strong>From Australia:</strong> <code className="font-mono">0011 {dialDigits} {exampleLocal}</code></p>
            <p className="text-muted-foreground">
              On a mobile or in any app that accepts <code className="font-mono">+</code>, just dial{" "}
              <code className="font-mono">{country.dialCode}</code> followed by the local number.
              If the local number starts with a 0, drop it.
            </p>
          </div>
        </section>

        {(rates.landline !== null || rates.mobile !== null) && (
          <section className="rounded-xl border bg-card p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">What it costs on elefa Connect</h2>
            <p className="text-muted-foreground text-sm mb-4">
              Per-minute rates to {country.name} come from the same price list the dialler uses.
              What you see here is what you pay. No connection fees, no monthly plan.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {rates.landline !== null && (
                <div className="rounded-lg bg-muted p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Landline</div>
                  <div className="text-2xl font-bold">{formatAudCents(rates.landline)}<span className="text-sm font-normal text-muted-foreground"> /min</span></div>
                </div>
              )}
              {rates.mobile !== null && (
                <div className="rounded-lg bg-muted p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Mobile</div>
                  <div className="text-2xl font-bold">{formatAudCents(rates.mobile)}<span className="text-sm font-normal text-muted-foreground"> /min</span></div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Prices shown in AUD. No hidden fees. Credit you top up doesn't expire.
            </p>

            <div className="mt-5">
              <Link to={`/connect-portal/${slug}`}>
                <Button size="lg">Call {country.name}</Button>
              </Link>
            </div>
          </section>
        )}

        {country.majorCities?.length > 0 && (
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-3">Which cities {country.dialCode} reaches</h2>
            <p className="text-muted-foreground">
              The {country.dialCode} code reaches any phone number in {country.name}, from {country.capital} to{" "}
              smaller towns. Major centres include {country.majorCities.join(", ")}. For landlines, an area code
              follows the country code; mobile numbers don't use area codes.
            </p>
          </section>
        )}


        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">Frequently asked questions</h2>
          <div className="space-y-4">
            <details className="rounded-lg border p-4">
              <summary className="font-medium cursor-pointer">What is the country code for {country.name}?</summary>
              <p className="mt-2 text-muted-foreground text-sm">
                {country.dialCode}. Dial your international access code, then {dialDigits}, then the local number.
              </p>
            </details>
            <details className="rounded-lg border p-4">
              <summary className="font-medium cursor-pointer">How do I dial a {country.name} number from abroad?</summary>
              <p className="mt-2 text-muted-foreground text-sm">
                Use the format <code className="font-mono">+{dialDigits} {exampleLocal}</code>. From the US prefix
                {" "}<code className="font-mono">011</code>, from Australia <code className="font-mono">0011</code>,
                from the UK and most of Europe <code className="font-mono">00</code>. Drop the leading 0 of the local number.
              </p>
            </details>
            {country.faqs
              .filter((f) => {
                const q = f.question.toLowerCase();
                // Skip duplicates of the two questions we always render above.
                return !q.includes("what is the country code") && !q.includes("how do i dial");
              })
              .map((f, i) => (
                <details key={i} className="rounded-lg border p-4">
                  <summary className="font-medium cursor-pointer">{f.question}</summary>
                  <p className="mt-2 text-muted-foreground text-sm">{f.answer}</p>
                </details>
              ))}
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Other country codes</h2>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {COUNTRY_CODE_SLUGS.filter((s) => s !== slug).map((s) => {
              const c = CONNECT_COUNTRIES[s];
              if (!c) return null;
              return (
                <li key={s}>
                  <Link to={`/country-code/${s}`} className="hover:underline">
                    {c.flag} {c.dialCode} {c.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      </main>

      <ConnectFooter />
    </div>
  );
}
